import { useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

// ---- date helpers (India time) ----
export const istTodayStr = () =>
  new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

export const fmtDMY = (s) => {
  if (!s) return "—";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : s;
};

export const isPastDate = (s) => (s ? String(s).slice(0, 10) < istTodayStr() : false);

// ---- status badge ----
const TONE = {
  submitted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  inactive: "bg-rose-100 text-rose-700 border-rose-200",
};
export function StatusBadge({ status, testid }) {
  const s = (status || "").toLowerCase();
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border capitalize ${TONE[s] || "bg-slate-100 text-slate-600 border-slate-200"}`}
    >
      {status || "—"}
    </span>
  );
}

// ---- generic searchable combobox ----
export function Combobox({ options, value, onChange, placeholder = "Select…", searchPlaceholder = "Search…", testid, disabled, emptyText = "No results." }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid}
          className="w-full inline-flex items-center justify-between h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm text-left disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#22a722]/40">
          <span className={selected ? "text-slate-800 truncate" : "text-slate-400"}>{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.value} value={o.label}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  onSelect={() => { onChange(o.value, o); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---- searchable department dropdown ----
export function DeptCombobox({ departments, value, onChange, testid, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className="w-full inline-flex items-center justify-between h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm text-left disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#22a722]/40"
        >
          <span className={value ? "text-slate-800" : "text-slate-400"}>
            {value || "Select department"}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search department…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No department found.</CommandEmpty>
            <CommandGroup>
              {departments.map((d) => (
                <CommandItem
                  key={d.id}
                  value={d.name}
                  data-testid={`dept-opt-${d.name}`}
                  onSelect={() => { onChange(d.name); setOpen(false); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === d.name ? "opacity-100" : "opacity-0"}`} />
                  {d.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
